import {
  faro,
  getWebInstrumentations,
  initializeFaro,
  isInternalFaroOnGlobalObject,
  type Instrumentation,
} from '@grafana/faro-web-sdk';
import { ReactIntegration } from '@grafana/faro-react';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { ADDRESS_API_URLS } from '@monofront/business/constants';
import { LOCALSTORAGE_COOKIE_CONSENT } from '@constants/localStorage';

type FaroConfig = {
  url?: string;
  apiKey?: string;
  env?: string;
  version?: string;
  sampleRate?: number;
  tracing?: boolean;
};

const DEFAULT_SAMPLE_RATE = 0.15;

// OneTrust ANALYTICS group (see OneTrustCookieCategories in Vendors.tsx —
// duplicated here to avoid pulling the whole Vendors module into this init
// path). Behavioral telemetry (user actions, tracing) must stay behind the
// same consent that already gates Faro user identification.
const ONETRUST_ANALYTICS = ',C0002,';

const hasStoredAnalyticsConsent = () => {
  try {
    const consent =
      (window as { OnetrustActiveGroups?: string }).OnetrustActiveGroups ??
      window.localStorage?.getItem(LOCALSTORAGE_COOKIE_CONSENT) ??
      '';

    return consent.includes(ONETRUST_ANALYTICS);
  } catch {
    return false;
  }
};

/**
 * Initializes the Grafana Faro Web SDK (frontend observability: JS errors,
 * web vitals, logs). Telemetry is collected by the self-hosted alloy-faro
 * receiver and lands in Loki ({source="faro"}).
 *
 * No-ops on the server and when no collector URL is configured (local dev).
 */
const initFaro = (config: FaroConfig = {}) => {
  // faro.api is a truthy noop stub before initialization, so it cannot be
  // used as an "already initialized" check — use the SDK's own guard.
  if (
    typeof window === 'undefined' ||
    !config.url ||
    isInternalFaroOnGlobalObject()
  ) {
    return;
  }

  // Faro expects a 0-1 rate (the legacy Datadog config used percentages, so
  // guard against out-of-range values from deployment config).
  const sampleRate = config.sampleRate;
  const samplingRate =
    typeof sampleRate === 'number' && sampleRate >= 0 && sampleRate <= 1
      ? sampleRate
      : DEFAULT_SAMPLE_RATE;

  // Consent is read once at startup from the persisted OneTrust state, so
  // returning visitors who opted in get the behavioral instrumentation from
  // the first page load; brand-new visitors get it on their next session.
  const analyticsConsent = hasStoredAnalyticsConsent();

  // FaroErrorBoundary (_app.tsx) reports through faro-react's own `api`
  // binding, which only ReactIntegration populates. Without it the boundary
  // throws on `api.pushError` while handling a render error, which masks that
  // error and escalates past React's recovery.
  const instrumentations: Instrumentation[] = [
    ...getWebInstrumentations(),
    new ReactIntegration(),
  ];

  if (config.tracing && analyticsConsent) {
    instrumentations.push(new TracingInstrumentation());
  }

  initializeFaro({
    url: config.url,
    apiKey: config.apiKey,
    app: {
      name: 'ecommerce',
      version: config.version,
      environment: config.env,
    },
    sessionTracking: {
      enabled: true,
      samplingRate,
    },
    // Keeps resource timings and fetch traces of these calls out of Faro.
    ignoreUrls: ADDRESS_API_URLS,
    userActionsInstrumentation: {
      // User actions (data-faro-user-action-name clicks) are behavioral:
      // only collected with ANALYTICS consent.
      excludeItem: () => !analyticsConsent,
    },
    instrumentations,
  });
};

/**
 * Sends a structured, queryable event (kind=event in Loki) — use for
 * business-critical failures instead of bare console/log strings.
 */
const pushFaroEvent = (name: string, attributes?: Record<string, string>) => {
  if (!faro.config) {
    return;
  }

  faro.api.pushEvent(name, attributes);
};

/**
 * Attaches an attribute to the current Faro session so it is present on all
 * subsequent telemetry (replaces Datadog's setGlobalContextProperty usage).
 * Passing undefined clears the attribute.
 */
const setFaroSessionAttribute = (key: string, value: string | undefined) => {
  // faro.config is only set once initializeFaro has run (faro.api is a noop
  // stub before that, so it is not a reliable initialization check).
  if (!faro.config) {
    return;
  }

  const session = faro.api.getSession();
  const attributes = { ...session?.attributes };

  if (value === undefined) {
    delete attributes[key];
  } else {
    attributes[key] = value;
  }

  faro.api.setSession({
    ...session,
    attributes,
  });
};

export { initFaro, pushFaroEvent, setFaroSessionAttribute };
